import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, LangToggle } from '../contexts/LanguageContext';
import api from '../services/api';
import { BRAND, GAME_BASE_URL } from '../config';
import { QRCodeSVG } from 'qrcode.react';

const G = '#00C060';
const G_DARK = '#00A651';
const BG_CARD = 'rgba(10, 30, 12, 0.95)';
const BORDER = 'rgba(0, 192, 96, 0.4)';

const Hostess = () => {
  const { user, logout } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', play_mode: 'whatsapp' });
  const [qtyNaturePower, setQtyNaturePower] = useState(0);
  const [qtyOther, setQtyOther] = useState(0);
  const [ticketPhoto, setTicketPhoto] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [gameToken, setGameToken] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const fileInputRef = useRef(null);
  const qrPollRef = useRef(null);

  const loadSessionInfo = useCallback(async () => {
    try {
      const response = await api.get('/hostess/active-session', { params: { brand: BRAND } });
      setSessionInfo(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || t('loadError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadSessionInfo(); }, [loadSessionInfo]);

  const totalBouteilles = qtyNaturePower + qtyOther;
  const tours = Math.floor(totalBouteilles / 2);
  const participationValide = totalBouteilles >= 2 && qtyNaturePower >= 1;

  const handleSubmit = async (mode) => {
    if (!formData.name) {
      setMessage({ type: 'danger', text: t('fillName') });
      return;
    }
    const phoneDigits = (formData.phone || '').replace(/\D/g, '');
    if (!phoneDigits) {
      setMessage({ type: 'danger', text: t('fillPhone') });
      return;
    }
    if (!/^[267]\d{8}$/.test(phoneDigits)) {
      setMessage({ type: 'danger', text: t('invalidPhone') });
      return;
    }
    const normalizedPhone = `+237${phoneDigits}`;
    if (!participationValide) {
      setMessage({ type: 'danger', text: t('needBottles') });
      return;
    }
    if (!ticketPhoto) {
      setMessage({ type: 'danger', text: t('needPhoto') });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const formDataObj = new FormData();
    formDataObj.append('name', formData.name);
    formDataObj.append('phone', normalizedPhone);
    formDataObj.append('play_mode', mode);
    formDataObj.append('qty_nature_power', qtyNaturePower);
    formDataObj.append('qty_other', qtyOther);
    formDataObj.append('brand', BRAND);
    formDataObj.append('lang', lang);
    formDataObj.append('ticket_photo', ticketPhoto);

    try {
      const response = await api.post('/hostess/register-participant', formDataObj);
      const token = response.data.game_token;

      if (mode === 'whatsapp') {
        const whatsappLink = response.data.whatsapp_link;
        setMessage({ type: 'success', text: t('registered') });
        if (whatsappLink) window.open(whatsappLink, '_blank');
        resetForm();
        loadSessionInfo();
      } else if (mode === 'qr_code') {
        if (token) {
          setGameToken(token);
          setShowQRCode(true);
          qrPollRef.current = setInterval(async () => {
            try {
              const res = await api.get(`/game/result/${token}`);
              if (res.data.token_opened) {
                clearInterval(qrPollRef.current);
                setShowQRCode(false);
                setGameToken(null);
                resetForm();
                loadSessionInfo();
              }
            } catch (_) {}
          }, 2000);
        } else {
          setMessage({ type: 'danger', text: t('tokenError') });
        }
      } else {
        if (!token) {
          setMessage({ type: 'danger', text: t('tokenError') });
          return;
        }
        setMessage({ type: 'success', text: t('redirecting') });
        setTimeout(() => navigate(`/play/${token}`), 500);
        return;
      }
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || t('registerError') });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', play_mode: 'whatsapp' });
    setQtyNaturePower(0);
    setQtyOther(0);
    setTicketPhoto(null);
    setTicketPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCloseQRCode = () => {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    setShowQRCode(false);
    setGameToken(null);
    resetForm();
    loadSessionInfo();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 900;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          const compressed = new File([blob], file.name, { type: 'image/jpeg' });
          setTicketPhoto(compressed);
          setTicketPreview(URL.createObjectURL(compressed));
        }, 'image/jpeg', 0.75);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    if (window.confirm(t('deletePhotoConfirm'))) {
      setTicketPhoto(null);
      setTicketPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const spinnerStyle = { background: `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)` };
  const labelStyle = { color: '#ffffff', fontWeight: '700', fontSize: '13px', marginBottom: '6px', display: 'block' };
  const inputStyle = { background: 'rgba(255,255,255,0.12)', borderColor: BORDER, color: '#ffffff', fontSize: '15px', fontWeight: '500' };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: `linear-gradient(160deg, #00C060 0%, #007A38 35%, #004d25 65%, #111111 100%)` }}>
        <div className="text-center text-white">
          <div className="mb-4">
            <div style={{ width: '60px', height: '60px', margin: '0 auto' }}>
              <div className="spinner-border" role="status" style={{ width: '100%', height: '100%', borderWidth: '4px' }}></div>
            </div>
          </div>
          <p className="mb-0 fw-bold">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!sessionInfo || !sessionInfo.active) {
    return (
      <div className="min-vh-100 d-flex flex-column" style={{ background: `linear-gradient(160deg, #00C060 0%, #007A38 35%, #004d25 65%, #111111 100%)` }}>
        <nav className="navbar navbar-dark" style={{ background: 'rgba(13,26,15,0.5)', backdropFilter: 'blur(10px)' }}>
          <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}></div>
            <img src="/logo-white.png" alt="Reaktor Nature Power" style={{ width: '140px', height: 'auto' }} />
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
              <LangToggle />
              <button onClick={handleLogout} className="btn btn-outline-light btn-sm">
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>
        </nav>
        <div className="container flex-grow-1 d-flex align-items-center py-3">
          <div className="card border-0 shadow-lg w-100" style={{ borderRadius: '20px', background: BG_CARD, border: `1px solid ${BORDER}` }}>
            <div className="card-body py-5 text-center">
              <div style={{ width: '70px', height: '70px', ...spinnerStyle, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <i className="fas fa-calendar-times" style={{ fontSize: '28px', color: '#fff' }}></i>
              </div>
              <h3 className="mb-2 fw-bold text-white">{t('noActiveSession')}</h3>
              <p className="text-light mb-3">{t('noActiveSessionDesc')}</p>
              <button onClick={loadSessionInfo} className="btn btn-lg px-4" style={{ ...spinnerStyle, color: '#fff', border: 'none', fontWeight: 'bold' }}>
                <i className="fas fa-sync-alt me-2"></i>{t('refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: `linear-gradient(160deg, #00C060 0%, #007A38 35%, #004d25 65%, #111111 100%)` }}>
      <nav className="navbar navbar-dark sticky-top" style={{ background: 'rgba(13,26,15,0.85)', backdropFilter: 'blur(10px)', padding: '8px 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}></div>
          <img src="/logo-white.png" alt="Reaktor Nature Power" style={{ width: '140px', height: 'auto' }} />
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
            <span className="text-white d-none d-md-block" style={{ textAlign: 'right' }}>
              <small style={{ fontSize: '10px', display: 'block' }}>{t('loggedAs')}</small>
              <strong style={{ fontSize: '13px' }}><i className="fas fa-user-tag me-1"></i>{user?.username}</strong>
            </span>
            <LangToggle />
            <button onClick={handleLogout} className="btn btn-outline-light btn-sm">
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </nav>

      <div className="container py-3">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3 shadow-sm`} role="alert" style={{ fontSize: '14px' }}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Session Info */}
        <div className="card border-0 shadow mb-3" style={{ borderRadius: '12px', background: BG_CARD, border: `1px solid ${BORDER}` }}>
          <div className="card-body py-2">
            <div className="row align-items-center g-2">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', ...spinnerStyle, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{t('session')}</small>
                    <strong className="d-block text-white" style={{ fontSize: '13px' }}>{sessionInfo.session_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: `rgba(0,166,81,0.2)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-store" style={{ color: G, fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{t('pdv')}</small>
                    <strong className="d-block text-white" style={{ fontSize: '13px' }}>{sessionInfo.pdv_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: `rgba(0,166,81,0.2)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-clock" style={{ color: G, fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small className="text-light d-block" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{t('schedule')}</small>
                    <strong className="d-block text-white" style={{ fontSize: '11px' }}>
                      {new Date(sessionInfo.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(sessionInfo.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        <div className="card border-0 shadow" style={{ borderRadius: '12px', background: BG_CARD, border: `1px solid ${BORDER}` }}>
          <div className="card-body p-3">
            <div className="d-flex align-items-center mb-3">
              <div style={{ width: '36px', height: '36px', ...spinnerStyle, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                <i className="fas fa-user-plus" style={{ color: '#fff', fontSize: '14px' }}></i>
              </div>
              <div>
                <h6 className="mb-0 fw-bold text-white" style={{ fontSize: '15px' }}>{t('newParticipant')}</h6>
                <small className="text-light" style={{ fontSize: '12px' }}>{t('registerClient')}</small>
              </div>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              {/* Photo ticket */}
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>
                  <i className="fas fa-camera me-2" style={{ color: G }}></i>{t('photoTicket')}
                </label>
                {ticketPreview ? (
                  <div className="position-relative">
                    <div className="card border-2" style={{ borderColor: G, background: `rgba(0,166,81,0.1)` }}>
                      <div className="card-body p-2 text-center">
                        <img src={ticketPreview} alt="Ticket" style={{ maxHeight: '150px', width: '100%', objectFit: 'contain', borderRadius: '8px' }} />
                        <div className="mt-2">
                          <span className="badge" style={{ background: G, color: '#fff', fontSize: '12px' }}>
                            <i className="fas fa-check-circle me-1"></i>{t('photoOk')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={clearPhoto} className="position-absolute top-0 end-0 btn btn-danger btn-sm shadow m-2" style={{ padding: '4px 8px', fontSize: '12px' }}>
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                ) : (
                  <>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="btn w-100 py-2 border-2"
                      style={{ background: 'transparent', borderColor: G, color: G, fontSize: '14px' }}>
                      <i className="fas fa-camera me-2"></i><span className="fw-semibold">{t('takePhoto')}</span>
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} style={{ display: 'none' }} />
                  </>
                )}
              </div>

              {/* Nom & Téléphone */}
              <div className="row g-2 mb-3">
                <div className="col-12 col-md-6">
                  <label className="form-label" style={labelStyle}>{t('nameLabel')}</label>
                  <div className="input-group">
                    <span className="input-group-text" style={{ background: `rgba(0,192,96,0.15)`, borderColor: BORDER, color: G, padding: '10px 12px' }}><i className="fas fa-user"></i></span>
                    <input type="text" className="form-control" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t('namePlaceholder')} required
                      style={inputStyle} />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label" style={labelStyle}>{t('phoneLabel')}</label>
                  <div className="input-group">
                    <span className="input-group-text" style={{ background: `rgba(0,192,96,0.15)`, borderColor: BORDER, color: G, padding: '10px 12px' }}><i className="fas fa-phone"></i></span>
                    <input type="tel" className="form-control" placeholder="6XXXXXXXX"
                      value={formData.phone}
                      onChange={(e) => {
                        let digits = (e.target.value || '').replace(/\D/g, '');
                        if (digits.startsWith('00237')) digits = digits.slice(5);
                        if (digits.startsWith('237')) digits = digits.slice(3);
                        setFormData({ ...formData, phone: digits.slice(0, 9) });
                      }}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Bouteilles */}
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>
                  {t('bottlesLabel')} <span style={{ color: G, fontSize: '12px', fontWeight: '600' }}>{t('bottlesHint')}</span>
                </label>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="card border-0" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
                      <div className="card-body p-2 text-center">
                        <small className="d-block mb-1" style={{ fontSize: '11px', color: G, fontWeight: '700' }}>Reaktor Nature Power</small>
                        <small className="text-light d-block mb-1" style={{ fontSize: '10px' }}>0,5L</small>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button type="button" className="btn btn-sm"
                            onClick={() => setQtyNaturePower(q => Math.max(0, q - 1))}
                            style={{ background: `rgba(0,166,81,0.2)`, border: `1px solid ${BORDER}`, color: G, width: '32px', height: '32px', padding: 0 }}>
                            <i className="fas fa-minus" style={{ fontSize: '10px' }}></i>
                          </button>
                          <span className="text-white fw-bold" style={{ fontSize: '20px', minWidth: '28px' }}>{qtyNaturePower}</span>
                          <button type="button" className="btn btn-sm"
                            onClick={() => setQtyNaturePower(q => q + 1)}
                            style={{ background: `rgba(0,166,81,0.2)`, border: `1px solid ${BORDER}`, color: G, width: '32px', height: '32px', padding: 0 }}>
                            <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="card border-0" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
                      <div className="card-body p-2 text-center">
                        <small className="d-block mb-1" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', fontWeight: '600' }}>{t('otherReaktor')}</small>
                        <small className="text-light d-block mb-1" style={{ fontSize: '10px' }}>0,5L</small>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button type="button" className="btn btn-sm"
                            onClick={() => setQtyOther(q => Math.max(0, q - 1))}
                            style={{ background: `rgba(0,166,81,0.2)`, border: `1px solid ${BORDER}`, color: G, width: '32px', height: '32px', padding: 0 }}>
                            <i className="fas fa-minus" style={{ fontSize: '10px' }}></i>
                          </button>
                          <span className="text-white fw-bold" style={{ fontSize: '20px', minWidth: '28px' }}>{qtyOther}</span>
                          <button type="button" className="btn btn-sm"
                            onClick={() => setQtyOther(q => q + 1)}
                            style={{ background: `rgba(0,166,81,0.2)`, border: `1px solid ${BORDER}`, color: G, width: '32px', height: '32px', padding: 0 }}>
                            <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {totalBouteilles > 0 && (
                  <div className="mt-2 p-2 rounded text-center" style={{
                    background: participationValide ? 'rgba(0,166,81,0.15)' : 'rgba(220,53,69,0.15)',
                    border: `1px solid ${participationValide ? G : '#dc3545'}`
                  }}>
                    <span className="fw-bold" style={{ color: participationValide ? '#6fcf6f' : '#ff6b6b', fontSize: '14px' }}>
                      {t('total')} : {totalBouteilles} {totalBouteilles > 1 ? t('bottlePlural') : t('bottle')}
                    </span>
                    {participationValide
                      ? <span className="text-light ms-2" style={{ fontSize: '12px' }}>→ {tours} {tours > 1 ? t('turnPlural') : t('turnOf')} {t('gameOf')}</span>
                      : <span className="text-light ms-2" style={{ fontSize: '12px' }}>→ {qtyNaturePower === 0 ? t('atLeastOneNP') : t('minTwo')}</span>
                    }
                  </div>
                )}
              </div>

              {/* Mode de jeu */}
              <div className="mb-2">
                <label className="form-label" style={labelStyle}>{t('gameModeLabel')}</label>
                <div className="row g-2">
                  <div className="col-4">
                    <button type="button" className="btn w-100 py-2" disabled={submitting}
                      style={{ background: '#25D366', color: '#fff', border: '2px solid #25D366', fontSize: '12px', fontWeight: 'bold', borderRadius: '10px' }}
                      onClick={() => handleSubmit('whatsapp')}>
                      <i className="fab fa-whatsapp me-1"></i>
                      <div className="fw-semibold">WhatsApp</div>
                      <small style={{ fontSize: '9px' }}>{t('sendToClient')}</small>
                    </button>
                  </div>
                  <div className="col-4">
                    <button type="button" className="btn w-100 py-2" disabled={submitting}
                      style={{ background: `linear-gradient(135deg, ${G} 0%, ${G_DARK} 100%)`, color: '#fff', border: `2px solid ${G}`, fontSize: '12px', fontWeight: 'bold', borderRadius: '10px' }}
                      onClick={() => handleSubmit('direct')}>
                      <i className="fas fa-mobile-alt me-1"></i>
                      <div className="fw-semibold">{t('playDirect')}</div>
                      <small style={{ fontSize: '9px' }}>{t('playOnMobile')}</small>
                    </button>
                  </div>
                  <div className="col-4">
                    <button type="button" className="btn w-100 py-2" disabled={submitting}
                      style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', color: '#fff', border: '2px solid #8B5CF6', fontSize: '12px', fontWeight: 'bold', borderRadius: '10px' }}
                      onClick={() => handleSubmit('qr_code')}>
                      <i className="fas fa-qrcode me-1"></i>
                      <div className="fw-semibold">QR Code</div>
                      <small style={{ fontSize: '9px' }}>{t('scan')}</small>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal QR Code */}
      {showQRCode && gameToken && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '20px' }}>
          <div style={{ background: `linear-gradient(160deg, #00C060 0%, #007A38 35%, #004d25 65%, #111111 100%)`, borderRadius: '20px', padding: '30px', maxWidth: '400px', width: '100%', textAlign: 'center', border: '2px solid rgba(255,255,255,0.2)' }}>
            <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
              <i className="fas fa-qrcode" style={{ fontSize: '24px', color: '#fff' }}></i>
            </div>
            <h2 style={{ color: '#fff', marginBottom: '10px', fontSize: '22px', fontWeight: 'bold' }}>{t('qrTitle')}</h2>
            <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '20px', fontSize: '14px' }}>
              {t('qrDesc')}
            </p>
            <div style={{ background: '#fff', padding: '20px', borderRadius: '15px', marginBottom: '20px', display: 'inline-block' }}>
              <QRCodeSVG value={`${GAME_BASE_URL}/play/${gameToken}`} size={200} level="H" includeMargin={true} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => window.open(`${GAME_BASE_URL}/play/${gameToken}`, '_blank')}
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                <i className="fas fa-external-link-alt me-2"></i>{t('testLink')}
              </button>
              <button onClick={handleCloseQRCode}
                style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                <i className="fas fa-times me-2"></i>{t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hostess;
